import React, { PropsWithChildren } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { Separator } from "@components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { useAuth } from "@components/auth-provider/AuthProvider";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { UserRole } from "@modules/authentication/roles";
import { Routes } from "~/routes";
import { cn } from "~/lib/utils";
import {
  ChevronRight,
  Globe2,
  LifeBuoy,
  LogOut,
  PanelLeftIcon,
  Settings,
  Shield,
  Briefcase,
  Users,
} from "lucide-react";
import { ModeToggle } from "@components/mode-toggle";
import pesLogoUrl from "@icons/PES_Logo.svg?url";
import { OrganisationSelector } from "@components/organisation-selector/OrganisationSelector";
import { MobileAppBanner } from "@components/mobile-app-banner/MobileAppBanner";
import { OpenInAppButton } from "@components/mobile-app-banner/OpenInAppButton";
import { usePageTitle } from "~/hooks/usePageTitle";
import { JobStatusIndicator } from "@components/background-jobs/JobStatusIndicator";

export type PageActionVariant = "primary" | "secondary" | "tertiary" | "link";
export type PageActionColor = "primary" | "success" | "error" | "teal";

export interface PageAction {
  text: string;
  action?: () => void;
  variant?: PageActionVariant;
  color?: PageActionColor;
  icon?: React.ReactElement;
  disabled?: boolean;
  className?: string;
  element?: React.ReactNode;
}

export interface Breadcrumb {
  label: string;
  path: string;
}

export interface SidebarItemSecondLevel {
  label: string;
  action?: string | (() => void);
}

export interface SidebarItemTopLevel {
  label: string;
  icon?: React.ReactNode;
  action?: string | (() => void);
  subItems?: SidebarItemSecondLevel[];
}

export interface PageTemplateProps extends PropsWithChildren {
  title?: string | React.ReactNode;
  browserTitle?: string;
  subtitle?: string;
  actions?: PageAction[];
  headerRule?: boolean;
  breadcrumbs?: Breadcrumb[];
  activeItem?: string;
  activeSubItem?: string;
}

const extractTextFromNode = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).join(" ");
  }

  if (React.isValidElement(node)) {
    return extractTextFromNode(node.props.children);
  }

  return "";
};

export const resolveBrowserTitle = (
  title?: string | React.ReactNode,
  browserTitle?: string
): string | undefined => {
  const trimmedBrowserTitle = browserTitle?.trim();
  if (trimmedBrowserTitle) {
    return trimmedBrowserTitle;
  }

  if (typeof title === "string") {
    const trimmedTitle = title.trim();
    return trimmedTitle || undefined;
  }

  const extracted = extractTextFromNode(title).replace(/\s+/g, " ").trim();
  return extracted || undefined;
};

const actionVariantMap: Record<
  PageActionVariant,
  React.ComponentProps<typeof Button>["variant"]
> = {
  primary: "default",
  secondary: "secondary",
  tertiary: "ghost",
  link: "link",
};

const getButtonVariant = (
  action: PageAction
): React.ComponentProps<typeof Button>["variant"] => {
  if (action.variant) {
    return actionVariantMap[action.variant];
  }

  if (action.color === "error") {
    return "destructive";
  }

  return "default";
};

const normalize = (value?: string) => value?.trim().toLowerCase() ?? "";

const getInitials = (value: string) => {
  if (!value) {
    return "?";
  }

  const parts = value
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .filter(Boolean);

  if (parts.length === 0) {
    return value.slice(0, 2).toUpperCase();
  }

  return parts.slice(0, 2).join("");
};

const renderIcon = (icon?: React.ReactNode) => {
  if (!icon) {
    return null;
  }

  if (!React.isValidElement(icon)) {
    return icon;
  }

  return React.cloneElement(icon, {
    // @ts-ignore
    className: cn("size-4", icon.props.className),
  });
};

interface NavMainProps {
  items: SidebarItemTopLevel[];
  activeItem?: string;
  activeSubItem?: string;
  onNavigate: (action?: string | (() => void)) => void | Promise<void>;
}

const NavMain: React.FC<NavMainProps> = ({
  items,
  activeItem,
  activeSubItem,
  onNavigate,
}) => {
  const normalizedItem = normalize(activeItem);
  const normalizedSubItem = normalize(activeSubItem);

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Navigation</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const itemActive =
              normalize(item.label) === normalizedItem ||
              item.subItems?.some(
                (subItem) => normalize(subItem.label) === normalizedSubItem
              );

            return (
              <Collapsible key={item.label} asChild defaultOpen={itemActive}>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => onNavigate(item.action)}
                    isActive={itemActive}
                    tooltip={item.label}
                  >
                    {renderIcon(item.icon)}
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                  {item.subItems && item.subItems.length > 0 ? (
                    <>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuAction className="data-[state=open]:rotate-90">
                          <ChevronRight className="size-4" />
                          <span className="sr-only">Toggle</span>
                        </SidebarMenuAction>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.subItems.map((subItem) => {
                            const subActive =
                              normalize(subItem.label) === normalizedSubItem;

                            return (
                              <SidebarMenuSubItem key={subItem.label}>
                                <SidebarMenuSubButton
                                  isActive={subActive}
                                  onClick={() => onNavigate(subItem.action)}
                                >
                                  <span>{subItem.label}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            );
                          })}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </>
                  ) : null}
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

interface NavSecondaryProps {
  items: SidebarItemTopLevel[];
  onNavigate: (action?: string | (() => void)) => void | Promise<void>;
  className?: string;
}

const NavSecondary: React.FC<NavSecondaryProps> = ({
  items,
  onNavigate,
  className,
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <SidebarGroup className={className}>
      <SidebarGroupLabel>Support</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.label}>
              <SidebarMenuButton onClick={() => onNavigate(item.action)}>
                {renderIcon(item.icon)}
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
};

interface NavUserProps {
  name: string;
  role?: string | null;
  avatarUrl?: string | null;
  onLogout: () => void;
}

const NavUser: React.FC<NavUserProps> = ({
  name,
  role,
  avatarUrl,
  onLogout,
}) => {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          size="lg"
          className={cn(
            "gap-3",
            "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
          )}
        >
          <Avatar
            className={cn(
              "h-9 w-9 rounded-lg",
              "group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:w-9"
            )}
          >
            {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
            <AvatarFallback className="rounded-lg">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-semibold">
              {name || "Loading..."}
            </span>
            {role ? (
              <span className="truncate text-xs text-muted-foreground">
                {role}
              </span>
            ) : null}
          </div>
        </SidebarMenuButton>
        <SidebarMenuAction
          onClick={onLogout}
          className="hover:text-destructive"
        >
          <LogOut className="size-4" />
          <span className="sr-only">Log out</span>
        </SidebarMenuAction>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};

interface SidebarNavigationProps {
  headerItems: SidebarItemTopLevel[];
  footerItems: SidebarItemTopLevel[];
  activeItem?: string;
  activeSubItem?: string;
  userName: string;
  userRole?: string | null;
  avatarUrl?: string | null;
  showJobStatus?: boolean;
  onLogout: () => void;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  headerItems,
  footerItems,
  activeItem,
  activeSubItem,
  userName,
  userRole,
  avatarUrl,
  showJobStatus,
  onLogout,
}) => {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile, toggleSidebar, state } = useSidebar();

  const handleNavigate = React.useCallback(
    async (action?: string | (() => void)) => {
      if (!action) {
        return;
      }

      if (typeof action === "function") {
        await action();
      } else {
        navigate(action);
      }

      if (isMobile) {
        setOpenMobile(false);
      }
    },
    [isMobile, navigate, setOpenMobile]
  );

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex w-full items-center gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:p-1 group-data-[collapsible=icon]:pl-[5px]">
              <SidebarMenuButton
                size="lg"
                className={cn(
                  "flex-1 w-auto gap-3",
                  "group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:w-auto group-data-[collapsible=icon]:gap-0"
                )}
              >
                <img
                  src={pesLogoUrl}
                  alt="PES Logo"
                  className="h-9 w-9 rounded-lg object-contain dark:invert"
                />
                <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">ScentsCheck</span>
                  <span className="truncate text-xs text-muted-foreground">
                    Admin Console
                  </span>
                </div>
              </SidebarMenuButton>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={toggleSidebar}
                className={cn(
                  "size-9 text-sidebar-foreground hover:bg-sidebar-accent/60 focus-visible:ring-0 focus-visible:ring-offset-0",
                  "group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:rounded-md group-data-[collapsible=icon]:p-0"
                )}
              >
                <PanelLeftIcon
                  className={cn(
                    "size-4",
                    state === "collapsed" && "-scale-x-100"
                  )}
                />
                <span className="sr-only">Toggle sidebar</span>
              </Button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain
          items={headerItems}
          activeItem={activeItem}
          activeSubItem={activeSubItem}
          onNavigate={handleNavigate}
        />
        <OpenInAppButton />
        {showJobStatus ? <JobStatusIndicator className="mt-auto" /> : null}
        <NavSecondary
          items={footerItems}
          onNavigate={handleNavigate}
          className={showJobStatus ? undefined : "mt-auto"}
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          name={userName}
          role={userRole}
          avatarUrl={avatarUrl}
          onLogout={onLogout}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

const renderActionIcon = (icon?: React.ReactElement) => {
  if (!icon) {
    return null;
  }

  return React.cloneElement(icon, {
    className: cn("size-4", icon.props.className),
  });
};

export const buildHeaderItems = (
  role?: UserRole | null
): SidebarItemTopLevel[] => {
  const items: SidebarItemTopLevel[] = [];

  if (role) {
    items.push({
      label: "My Tests",
      icon: <Globe2 />,
      action: Routes.UserDashboard,
    });
  }

  if (role === UserRole.SuperAdmin || role === UserRole.Admin) {
    items.push(
      {
        label: "Soil Samples",
        icon: <Globe2 />,
        action: Routes.SoilSamples,
        subItems: [
          {
            label: "List",
            action: Routes.SoilSamples,
          },
          {
            label: "Create",
            action: Routes.SoilSampleCreate,
          },
        ],
      },
      {
        label: "Admin",
        icon: <Briefcase />,
        action: Routes.Admin,
        subItems: [
          {
            label: "Dashboard",
            action: Routes.AdminDashboard,
          },
          {
            label: "Customers",
            action: Routes.Customers,
          },
          {
            label: "Cassettes",
            action: Routes.AdminCassettes,
          },
          {
            label: "TROs",
            action: Routes.AdminTROs,
          },
          {
            label: "Users",
            action: Routes.AdminUsers,
          },
          {
            label: "Localisation",
            action: Routes.AdminLocalisation,
          },
          {
            label: "Product setup",
            action: Routes.ProductSetup,
          },
          {
            label: "Firmware",
            action: Routes.AdminFirmware,
          },
          {
            label: "Test Files",
            action: Routes.PesTests,
          },
        ],
      }
    );

    if (role === UserRole.SuperAdmin) {
      items.push({
        label: "User Management",
        icon: <Users />,
        action: Routes.UserManagement,
      });

      items.push({
        label: "Super admin",
        icon: <Shield />,
        action: Routes.TrainedIndicators,
        subItems: [
          {
            label: "Trained Indicators",
            action: Routes.TrainedIndicators,
          },
          {
            label: "Localisation setup",
            action: Routes.SuperAdminLocalisation,
          },
          {
            label: "Feature Extraction",
            action: Routes.FeatureCacheAdmin,
          },
          {
            label: "Audit Logs",
            action: Routes.AuditLogs,
          },
          {
            label: "Debug",
            action: Routes.Debug,
          },
        ],
      });
    }
  }

  return items;
};

export const PageTemplate: React.FC<PageTemplateProps> = (props) => {
  const { currentUser, cachedRole, cachedName } = useCurrentUser();
  const role = currentUser?.role || cachedRole;
  const auth = useAuth();
  const { pathname } = useLocation();
  const showOrgSelector = !pathname.startsWith("/admin");
  usePageTitle(resolveBrowserTitle(props.title, props.browserTitle));

  const breadcrumbs = props.breadcrumbs ?? [];
  const hasBreadcrumbs = breadcrumbs.length > 0;
  const actions = props.actions ?? [];

  const headerItems = React.useMemo<SidebarItemTopLevel[]>(
    () => buildHeaderItems(role),
    [role]
  );

  const footerItems = React.useMemo<SidebarItemTopLevel[]>(
    () => [
      {
        label: "Profile settings",
        action: Routes.ProfileSettings,
        icon: <Settings />,
      },
      {
        label: "Help",
        action: () =>
          window.open("https://pestechnologies.com/support/", "_blank"),
        icon: <LifeBuoy />,
      },
    ],
    []
  );

  const userName =
    currentUser?.fullName || currentUser?.email || cachedName || "";
  const showJobStatus = role === UserRole.Admin || role === UserRole.SuperAdmin;

  return (
    <SidebarProvider className="flex min-h-screen w-full bg-background">
      <SidebarNavigation
        headerItems={headerItems}
        footerItems={footerItems}
        activeItem={props.activeItem}
        activeSubItem={props.activeSubItem}
        userName={userName}
        userRole={currentUser?.role ?? cachedRole ?? null}
        avatarUrl={currentUser?.profilePictureUrl ?? null}
        showJobStatus={showJobStatus}
        onLogout={auth.logout}
      />
      <SidebarInset className="flex min-w-0 flex-1 flex-col">
        <MobileAppBanner />
        <header className="flex flex-col gap-4 border-b bg-background/80 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:px-8">
          <div className="flex items-center justify-between gap-3">
            <div
              className={cn(
                "flex items-center gap-3",
                !hasBreadcrumbs && "md:hidden"
              )}
            >
              <SidebarTrigger className="md:hidden" />
              {hasBreadcrumbs && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {breadcrumbs.map((breadcrumb, index) => {
                      const isLast = index === breadcrumbs.length - 1;

                      return (
                        <React.Fragment
                          key={`${breadcrumb.path}-${breadcrumb.label}`}
                        >
                          <BreadcrumbItem>
                            {isLast ? (
                              <BreadcrumbPage>
                                {breadcrumb.label}
                              </BreadcrumbPage>
                            ) : (
                              <BreadcrumbLink asChild>
                                <Link to={breadcrumb.path}>
                                  {breadcrumb.label}
                                </Link>
                              </BreadcrumbLink>
                            )}
                          </BreadcrumbItem>
                          {!isLast && <BreadcrumbSeparator />}
                        </React.Fragment>
                      );
                    })}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
            <div className="flex items-center gap-2">
              {showOrgSelector && <OrganisationSelector />}
              <ModeToggle />
            </div>
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col items-start gap-1 text-left">
              {props.title &&
                (typeof props.title === "string" ? (
                  <h1 className="text-left text-2xl font-semibold text-foreground">
                    {props.title}
                  </h1>
                ) : (
                  props.title
                ))}
              {props.subtitle && (
                <p className="text-left text-sm text-muted-foreground">
                  {props.subtitle}
                </p>
              )}
            </div>
            {actions.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {actions.map((action) =>
                  action.element ? (
                    <React.Fragment key={action.text}>
                      {action.element}
                    </React.Fragment>
                  ) : (
                    <Button
                      key={action.text}
                      type="button"
                      onClick={action.action}
                      variant={getButtonVariant(action)}
                      disabled={action.disabled}
                      className={cn("whitespace-nowrap", action.className)}
                    >
                      {renderActionIcon(action.icon)}
                      <span>{action.text}</span>
                    </Button>
                  )
                )}
              </div>
            )}
          </div>
          {props.headerRule && <Separator />}
        </header>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 flex-1 px-4 py-6 md:px-8">
            <div className="min-w-0 flex-1 flex flex-col gap-4">
              {props.children}
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
